import { useEffect, useState } from 'react';
import './ApiPhoto.css';

/**
 * A photo the API streams to its own people only (a delivery's parcel, InGo's
 * Deliveries): fetched with the account's token, shown from an object URL
 * that is revoked when it goes. Never a link that could be passed on.
 *
 * `load` returns a Promise of a Blob; keep it stable (`useCallback`), since
 * a new `load` fetches again.
 */
export function useApiPhoto(load) {
  const [photo, setPhoto] = useState({ state: 'loading', url: null });

  useEffect(() => {
    let url = null;
    let gone = false;
    setPhoto({ state: 'loading', url: null });

    Promise.resolve()
      .then(load)
      .then(
        (blob) => {
          if (gone) return;
          url = URL.createObjectURL(blob);
          setPhoto({ state: 'ready', url });
        },
        () => {
          if (!gone) setPhoto({ state: 'failed', url: null });
        },
      );

    return () => {
      gone = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [load]);

  return photo;
}

/** The photo, or a quiet line while it loads or when it could not be loaded. */
export default function ApiPhoto({ load, alt, className = '' }) {
  const photo = useApiPhoto(load);

  if (photo.state === 'ready') return <img src={photo.url} alt={alt} className={className} />;

  return (
    <p className="api-photo__note" role="status">
      {photo.state === 'loading' ? 'Loading the photo…' : 'The photo could not be loaded.'}
    </p>
  );
}
